import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Build timestamp for cache busting - updated on each deploy
const BUILD_TIMESTAMP = '2026-02-04T12:00:00Z';

// Supabase configuration
// IMPORTANT: Replace these with your actual Supabase project credentials
// You can find these in your Supabase project settings
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Debug logging for production troubleshooting
if (typeof window !== 'undefined') {
  console.log('[SOP App] Build timestamp:', BUILD_TIMESTAMP);
  console.log('[SOP App] Supabase URL configured:', !!supabaseUrl && supabaseUrl.includes('supabase.co'));
  console.log('[SOP App] Supabase Key configured:', !!supabaseAnonKey && supabaseAnonKey.length > 20);
}

/**
 * The local fixture backend (scripts/dev-backend) serves the same REST, auth and
 * storage surface from localhost, so that the staff pages and the portal's
 * section pages can be looked at — and audited on a phone — without a real
 * project. The `supabase.co` test below would reject it, and a check nobody can
 * satisfy locally is how the signed-in half of this app went unmeasured.
 *
 * Narrow on purpose, and narrow in two directions at once: a loopback host AND
 * a build that is not production. Neither alone would do. NODE_ENV is set by
 * react-scripts, not by .env, so `npm run build` cannot be talked into this by
 * a stray variable — the same reasoning as DEMO_ALLOWED in usePortalCards,
 * where the rule is that an env var is not a safety mechanism and a production
 * build is.
 */
const isLoopbackBackend = (url: string) =>
  process.env.NODE_ENV !== 'production' &&
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(url);

// Helper function to check if Supabase is configured
export const isSupabaseConfigured = () => {
  const configured = (
    supabaseUrl &&
    supabaseAnonKey &&
    supabaseUrl !== 'YOUR_SUPABASE_URL' &&
    supabaseAnonKey !== 'YOUR_SUPABASE_ANON_KEY' &&
    (supabaseUrl.includes('supabase.co') || isLoopbackBackend(supabaseUrl))
  );

  if (typeof window !== 'undefined') {
    console.log('[SOP App] isSupabaseConfigured:', configured);
  }

  return configured;
};

// Flag to indicate if activity_logs table exists in Supabase
// Set to true to enable activity logging to Supabase database
export const hasActivityLogsTable = true;

// Create Supabase client only if properly configured
// Otherwise, create a mock client to prevent crashes
let supabaseClient: SupabaseClient | null = null;

if (isSupabaseConfigured()) {
  try {
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'sop-app-auth', // Custom storage key to avoid conflicts
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      },
      db: {
        schema: 'public',
      },
      // No custom global headers here, deliberately.
      //
      // This used to send 'x-application-name: sop-app' on every request. It was
      // decorative — nothing read it, in the functions or the database — and it
      // silently broke every browser call to an Edge Function. A header the
      // function's CORS preflight does not list makes the browser refuse to send
      // the real request: you get an OPTIONS 200, no POST at all, and "Failed to
      // send a request to the Edge Function" on the client. That took out Add
      // Team Member, Reset password and Google connect.
      //
      // The functions now also reflect the requested headers, so this would no
      // longer break them. Adding a global header back still means checking
      // every function's Access-Control-Allow-Headers first.
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
    console.log('[SOP App] Supabase client created successfully');
  } catch (error) {
    console.error('[SOP App] Error creating Supabase client:', error);
    supabaseClient = null;
  }
}

// Export the client (will be null if not configured)
export const supabase = supabaseClient as any;

// Export types for TypeScript
export type { User as SupabaseUser, Session as SupabaseSession } from '@supabase/supabase-js';
